/*
 * Cottons and Blues: keep the header frosted for as long as it overlaps the
 * hero photograph, and let it go solid only once the hero has scrolled past.
 *
 * Horizon has no state for "the header is over the hero". It has
 * `data-sticky-state`, which flips to `active` the moment the page scrolls at
 * all, roughly 40px in. Every one of Horizon's transparent-header rules is
 * keyed to that attribute, so on this build the header dropped its glass and
 * repainted solid Cream while some 600px of hero was still sitting behind it:
 * an opaque bar cutting straight across the photograph.
 *
 * So this adds the state Horizon is missing. `cb-over-hero` is on the header
 * whenever the hero's bottom edge is still below the header's own bottom edge,
 * which is the same geometry test the Flush theme uses for `is-over-hero`.
 * assets/cottons-and-blues.css keys the glass to that class instead of to
 * `data-sticky-state`, so the two states now switch where the design says they
 * should: frosted over the photograph, solid over page content.
 *
 * The same measurement drives `cb-scrolled`, which is on the header from the
 * moment the page has moved at all and is what shrinks the wordmark lockup
 * down to the wordmark (see the logo crop in assets/cottons-and-blues.css).
 * That could not be hung off Horizon's `data-sticky-state` either: with
 * enable_sticky_header set to "always" the header never leaves the viewport,
 * so the intersection observer that would write the attribute never fires and
 * the attribute is simply absent on this build. Verified in the browser, where
 * document.querySelectorAll('[data-sticky-state]') is empty at every scroll
 * position.
 *
 * Loaded deferred from layout/theme.liquid.
 */
(function initHeaderScrollStates() {
  const SELECTOR_HEADER = '.header';
  /* Horizon's own wrapper class for the hero section, not a template block key,
     so this survives the homepage blocks being renamed or re-keyed. */
  const SELECTOR_HERO = '.hero-wrapper';
  /* Far enough in that a trackpad twitch or an elastic overscroll does not
     start the shrink, close enough that it reads as immediate. */
  const SCROLLED_AT = 24;

  let header = null;
  let hero = null;
  let frame = null;

  /*
   * Above 990px Horizon makes .page-wrapper the scroll container and pins
   * html/body to the viewport height; below it the document scrolls as usual.
   * Taking the larger of the two reads correctly on both sides of that
   * breakpoint without having to work out which one is live.
   */
  function scrollOffset() {
    const wrapper = document.querySelector('.page-wrapper');
    return Math.max(window.scrollY || 0, wrapper ? wrapper.scrollTop : 0);
  }

  function measure() {
    frame = null;
    if (!header) return;

    header.classList.toggle('cb-scrolled', scrollOffset() > SCROLLED_AT);

    if (hero) {
      const heroBottom = hero.getBoundingClientRect().bottom;
      const headerBottom = header.getBoundingClientRect().bottom;
      header.classList.toggle('cb-over-hero', heroBottom > headerBottom);
    }

    enableTransitions();
  }

  /*
   * The crossfade between the two header states must not play on first paint.
   * This script runs deferred, so the header renders in Horizon's solid state
   * for a frame before cb-over-hero lands: with transitions live from the
   * start, every homepage load would open with a visible solid-to-frosted
   * wipe. So the transitions are gated behind their own class and only armed
   * on the frame after the first measurement, once the correct state is
   * already painted.
   */
  function enableTransitions() {
    if (!header || header.classList.contains('cb-header-transitions')) return;
    requestAnimationFrame(() => {
      if (header) header.classList.add('cb-header-transitions');
    });
  }

  function schedule() {
    if (frame === null) frame = requestAnimationFrame(measure);
  }

  function bind() {
    header = document.querySelector(SELECTOR_HEADER);
    hero = document.querySelector(SELECTOR_HERO);
    if (!header) return;
    /* No hero on this template, so there is nothing to sit over. Clear the
       class rather than leaving a stale one behind after a section re-render.
       The scrolled state still runs: the logo crop applies on every template. */
    if (!hero) header.classList.remove('cb-over-hero');
    measure();
  }

  bind();

  /* Scroll events do not reach window when .page-wrapper is the scroller, so
     listen on document in the capture phase and catch it either way. */
  document.addEventListener('scroll', schedule, { capture: true, passive: true });
  window.addEventListener('resize', schedule, { passive: true });
  window.addEventListener('load', schedule);

  /* The theme editor swaps section markup in place, which throws away the
     element references above and any class we had put on them. */
  document.addEventListener('shopify:section:load', bind);
  document.addEventListener('shopify:section:unload', bind);
  document.addEventListener('shopify:section:reorder', bind);
})();

/*
 * Policies page: make anchored links work against collapsed accordions.
 *
 * The open and close itself is native <details> and needs no JS. Two things do,
 * and both are about arriving from somewhere else: the footer links straight to
 * policies#privacy-policy, #refund-policy and #shipping-policy, and those URLs
 * get shared.
 *
 * 1. Arriving at a fragment has to OPEN the policy it points at. Collapsed, a
 *    link to #refund-policy would otherwise land on a closed summary row and
 *    look like it had done nothing.
 *
 * 2. The scroll has to land ON the heading, not under the sticky header. CSS
 *    scroll-margin-top does that, but only once the panel is open and the layout
 *    has settled, which is why the scroll is reissued a frame later rather than
 *    left to the browser's initial jump against the closed layout.
 *
 * The in-page nav these were originally written for has been removed; this still
 * runs because the footer and any shared link depend on it.
 */
(function initPolicyAccordions() {
  const body = document.querySelector('.cb-policies-body');
  if (!body) return;

  function targetFromHash(hash) {
    if (!hash || hash === '#') return null;
    /* getElementById needs no escaping, unlike a selector, and the id comes from
       Liquid's `handle` filter so it is already safe. The class check is what
       matters: it confirms the fragment resolved to one of our policies rather
       than to some other element that happens to share the id. */
    const el = document.getElementById(decodeURIComponent(hash.slice(1)));
    return el && el.classList.contains('cb-policy') ? el : null;
  }

  function onHash() {
    const el = targetFromHash(window.location.hash);
    if (!el) return;
    el.open = true;
    /* Wait a frame so the opened panel is in the layout before the browser
       measures scroll-margin-top against it. */
    requestAnimationFrame(() => el.scrollIntoView({ block: 'start', behavior: 'smooth' }));
  }

  /* On first load the browser has already jumped, so open and re-scroll once to
     correct for the header offset against the now-open panel. */
  if (window.location.hash) onHash();
  window.addEventListener('hashchange', onHash);
})();
